export default {
  // Common
  app: {
    name: 'Coloursense',
    tagline: 'Discover Your Perfect Colors',
  },
  common: {
    back: 'Back',
    cancel: 'Cancel',
    save: 'Save',
    share: 'Share',
    create: 'Create',
    delete: 'Delete',
    edit: 'Edit',
    loading: 'Loading...',
    error: 'An error occurred',
    success: 'Success!',
    tryAgain: 'Try Again',
    reset: 'Reset',
  },
  accessibility: {
    title: 'Accessibility Settings',
    visualTitle: 'Visual Settings',
    audioTitle: 'Audio Settings',
    colorBlindMode: 'Color Blind Mode',
    colorBlindModeDesc:
      'Adjust colors to be more distinguishable for color blind users',
    colorBlindType: 'Type of Color Blindness',
    protanopia: 'Protanopia (Red-Blind)',
    deuteranopia: 'Deuteranopia (Green-Blind)',
    tritanopia: 'Tritanopia (Blue-Blind)',
    achromatopsia: 'Achromatopsia (No Color)',
    highContrast: 'High Contrast',
    highContrastDesc: 'Increase contrast between text and background',
    largeText: 'Large Text',
    largeTextDesc: 'Increase text size throughout the app',
    voiceGuidance: 'Voice Guidance',
    voiceGuidanceDesc:
      'Enable spoken feedback for app navigation and color analysis',
    showColorNames: 'Show Color Names',
    showColorNamesDesc: 'Always display color names alongside color swatches',
    resetSettings: 'Reset Accessibility Settings',
    resetTitle: 'Reset Settings',
    resetConfirm:
      'Are you sure you want to reset all accessibility settings to default?',
  },

  // Auth
  auth: {
    login: 'Login',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    forgotPassword: 'Forgot Password?',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    logout: 'Logout',
  },

  // Home Screen
  home: {
    features: 'Features',
    startAnalysis: 'Start Color Analysis',
    comingSoon: 'Coming Soon',
  },

  // Color Analysis
  colorAnalysis: {
    title: 'Color Analysis',
    upload: 'Upload Photo',
    takePhoto: 'Take Photo',
    analyzing: 'Analyzing your colors...',
    results: 'Analysis Results',
    yourPalette: 'Your Color Palette',
  },

  // Virtual Drape Test
  drapeTest: {
    title: 'Virtual Drape Test',
    subtitle: 'Test Colors Against Your Face',
    uploadSelfie:
      'Upload a selfie and compare how different colors look next to your face',
    chooseGallery: 'Choose from Gallery',
    takeSelfie: 'Take Selfie',
    changeImage: 'Change Image',
    colorPalettes: 'Color Palettes',
    tapColor: 'Tap a color to see how it looks against your face',
    analysis: 'Color Analysis',
    howWorks: 'How this color works for you:',
    selectColor: 'Select a color above to see personalized feedback',
    createCard: 'Create Shareable Color Card',
  },

  // Smart Wardrobe
  wardrobe: {
    title: 'Smart Wardrobe',
    yourPalette: 'Your Color Palette',
    yourItems: 'Your Clothing Items',
    addItem: '+ Add Item',
    addNew: 'Add New Clothing Item',
    uploadPhoto: 'Upload clothing photo',
    itemName: 'Item Name',
    tags: 'Tags (comma separated)',
    addToWardrobe: 'Add to Wardrobe',
    paletteMatch: 'Palette Match:',
  },

  // Wear This Today
  outfits: {
    title: 'Wear This Today',
    weather: "Today's Weather",
    humidity: 'Humidity',
    wind: 'Wind',
    recommended: "Today's Recommended Outfit",
    generating: 'Generating perfect outfit...',
    generateAnother: 'Generate Another Outfit',
    whyChosen: 'Why We Chose This',
  },

  // Premium Features
  premium: {
    title: 'Unlock Premium Features',
    subtitle: 'Upgrade to Coloursense Premium for full access to all features',
    smartWardrobe: 'Smart Wardrobe',
    wardrobeDesc: 'Tag your clothes and discover what fits your color palette',
    wearToday: 'Wear This Today',
    wearTodayDesc:
      'Get AI-powered outfit suggestions based on weather and your colors',
    unlimited: 'Unlimited Analysis',
    unlimitedDesc:
      'Create unlimited color profiles and save your analysis history',
    noAds: 'No Ads',
    noAdsDesc: 'Enjoy an ad-free experience throughout the app',
    monthly: 'Monthly',
    yearly: 'Yearly',
    save: 'Save 30%',
    perMonth: 'per month',
    perYear: 'per year',
    upgradeButton: 'Upgrade to Premium',
    processing: 'Processing...',
    restore: 'Restore Purchases',
    terms:
      'Payment will be charged to your Apple ID account at the confirmation of purchase. Subscription automatically renews unless it is canceled at least 24 hours before the end of the current period. Your account will be charged for renewal within 24 hours prior to the end of the current period. You can manage and cancel your subscriptions by going to your account settings on the App Store after purchase.',
  },

  // Subscription Context
  subscription: {
    freePlan: 'Free Plan',
    premiumPlan: 'Premium Plan',
    upgradeSuccess: 'Successfully upgraded to Premium!',
    upgradeError: 'Failed to upgrade to Premium',
    restoreSuccess: 'Successfully restored purchases!',
    restoreError: 'Failed to restore purchases',
    premiumRequired: 'Premium Required',
    premiumFeature: 'This is a premium feature',
    upgradeToAccess: 'Upgrade to Premium to access this feature',
    goToPremium: 'Go to Premium',
    continueFree: 'Continue with Free',
  },

  // Shareable Cards
  shareableCard: {
    title: 'Your Color Card',
    cannotCreate: "Can't Create Card",
    selectImageColor: 'Please select an image and a color first.',
    saved: 'Card saved to your photos!',
    shareTitle: 'Share Your Color Card',
    notAvailable: 'Sharing not available',
    notAvailableDesc: 'Sharing is not available on your device',
    error: 'Failed to share your color card.',
    myAnalysis: 'My Color Analysis',
    createdWith: 'Created with Coloursense',
  },

  // Lighting Conditions
  lighting: {
    title: 'Lighting Conditions',
    standard: {
      name: 'Standard',
      description: 'Balanced indoor lighting, suitable for most environments',
    },
    tropical: {
      name: 'Tropical',
      description: 'Bright, warm sunlight with high intensity',
    },
    nordic: {
      name: 'Nordic',
      description: 'Cool, blue-tinted natural light common in northern regions',
    },
    office: {
      name: 'Office',
      description: 'Fluorescent lighting typical in office environments',
    },
    evening: {
      name: 'Evening',
      description: 'Warm, golden lighting during sunset or with indoor lamps',
    },
    selectLighting: 'Select Lighting Condition',
    adjustPalette: 'Adjust palette for different lighting',
  },
};
